/**
 * Irish statutory National Minimum Wage, by age band and effective date.
 *
 * Mirrors the backend table in `src/modules/payroll/ireland/ireland-nmw.ts`,
 * which is what actually raises an NMW-breach error on a draft payroll. This
 * copy exists only so the Revenue Settings screen can display the same numbers;
 * if the two ever disagree, the backend wins and this file is the bug.
 *
 * The rates on this screen had been frozen at Ireland's 2023 values (€11.30 for
 * 20-and-over) since the screen was built, so an Irish employer read a figure
 * nearly three euros under the rate their payroll was actually being checked
 * against. Keeping it a plain dated table means each January is a two-line
 * change here and in the backend, not a re-derivation.
 *
 * Ireland's bands are purely age-based since the 2024 abolition of the
 * experience-based sub-minima: under 18 gets 70% of the full rate, 18 gets 80%,
 * 19 gets 90%, and 20-and-over gets 100%.
 */
export interface IrelandNmwBand {
  ageGroup: string;
  hourlyRateOfPay: number;
}

export interface IrelandNmwTable {
  effectiveFrom: string;
  bands: IrelandNmwBand[];
}

export const IRELAND_NMW_TABLES: IrelandNmwTable[] = [
  {
    effectiveFrom: '2025-01-01',
    bands: [
      { ageGroup: 'Age 20 and over', hourlyRateOfPay: 13.5 },
      { ageGroup: 'Age 19', hourlyRateOfPay: 12.15 },
      { ageGroup: 'Age 18', hourlyRateOfPay: 10.8 },
      { ageGroup: 'Age under 18', hourlyRateOfPay: 9.45 },
    ],
  },
  {
    effectiveFrom: '2026-01-01',
    bands: [
      { ageGroup: 'Age 20 and over', hourlyRateOfPay: 14.15 },
      { ageGroup: 'Age 19', hourlyRateOfPay: 12.74 },
      { ageGroup: 'Age 18', hourlyRateOfPay: 11.32 },
      { ageGroup: 'Age under 18', hourlyRateOfPay: 9.91 },
    ],
  },
];

/**
 * The bands in force on `date`. A date before the earliest table falls back to
 * that earliest table rather than rendering an empty screen — the same
 * throw-free choice the backend makes.
 */
export const getIrelandNmwBands = (
  date: Date = new Date(),
): IrelandNmwBand[] => {
  const iso = date.toISOString().slice(0, 10);
  const applicable = IRELAND_NMW_TABLES.filter((t) => t.effectiveFrom <= iso);
  const table = applicable.length
    ? applicable[applicable.length - 1]
    : IRELAND_NMW_TABLES[0];
  return table.bands;
};
